import { useLingui } from "@lingui/react/macro"
import React from "react"

import Stack from "../../../../design-system/stack"
import { isString } from "../../../../utils/types"
import { CaseStudyPageContext } from "../../types"
import {
  CASE_STUDY_DISPLAY_DATA_MAP,
  CaseStudyInfoKey,
  DisplayData,
} from "./data"
import * as styles from "./index.module.scss"
import MetadataInfo from "./metadata-info"

export type CaseStudyMetadataProps = Pick<
  CaseStudyPageContext,
  CaseStudyInfoKey
>

function useLocalizedCaseStudyDisplayDataLabel() {
  const { t } = useLingui()

  return (key: CaseStudyInfoKey): string => {
    switch (key) {
      case "companyIndustry":
        return t`Industry`
      case "companySize":
        return t`Company size`
      case "countriesSentTo":
        return t`Payouts sent to`
      case "averagePayoutValue":
        return t`Average payout value`
      case "productsUsed":
        return t`Products used`
      default:
        throw new Error(`Unknown case study info key: ${key}`)
    }
  }
}

function CaseStudyMetadata(props: CaseStudyMetadataProps) {
  const getLabelForKey = useLocalizedCaseStudyDisplayDataLabel()

  const entries = Object.entries(CASE_STUDY_DISPLAY_DATA_MAP) as [
    CaseStudyInfoKey,
    DisplayData,
  ][]

  return (
    <div className={styles.container}>
      <Stack spacing={32}>
        {entries.map(([key, displayData]) => {
          const { [key]: value } = props
          if (!value || (Array.isArray(value) && value.length === 0))
            return null

          const parsedValue =
            isString(value) && value.startsWith("[")
              ? JSON.parse(value).join(", ")
              : value
          const formattedValue =
            !isString(parsedValue) && key === "averagePayoutValue"
              ? `$${parsedValue}`
              : parsedValue

          return (
            <MetadataInfo
              key={key}
              iconType={displayData.iconType}
              label={getLabelForKey(key)}
              value={formattedValue}
            />
          )
        })}
      </Stack>
    </div>
  )
}

export default CaseStudyMetadata
